import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { setMiniSidebar, setOffcanvas } from '../../actions/settingsAction';
import { loggedOut } from '../../actions/authAction';
import * as jwtService from '../../services/jwt.service';
import MyOpinionLogo from '../../assets/images/MyOpinion_Logo_Head.png';

const DESKTOP_MIN_WIDTH = 992;
const COLLAPSED_KEY = 'customercare_sidebar_collapsed';

const initials = (user) =>
    `${(user?.prenom || '').charAt(0)}${(user?.nom || '').charAt(0)}`.toUpperCase() || '?';

/** Barre supérieure : bouton de menu (réduit la barre latérale sur ordinateur, l'ouvre sur mobile), langue, compte. */
const Header = ({ user, offcanvas, miniSidebar, setOffcanvas, setMiniSidebar, loggedOut }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [progress, setProgress] = useState(0);

    // Barre de progression de lecture de la page.
    useEffect(() => {
        const onScroll = () => {
            const el = document.documentElement;
            const total = el.scrollHeight - el.clientHeight;
            setProgress(total > 0 ? (el.scrollTop / total) * 100 : 0);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Préférence « barre latérale réduite » mémorisée dans le navigateur.
    useEffect(() => {
        try {
            if (localStorage.getItem(COLLAPSED_KEY) === '1') setMiniSidebar(true);
        } catch (e) { /* stockage indisponible : sans conséquence */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleSidebar = () => {
        if (window.innerWidth >= DESKTOP_MIN_WIDTH) {
            const next = !miniSidebar;
            setMiniSidebar(next);
            try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0'); } catch (e) { /* idem */ }
        } else {
            setOffcanvas(!offcanvas);
        }
    };

    const logout = () => {
        jwtService.remove(loggedOut);
        history.replace('/login');
    };

    const roleLabel = user?.isRoot ? t('Super administrateur') : user?.isAdmin ? t('Administrateur') : t('Modérateur');
    const fullName = `${user?.prenom || ''} ${user?.nom || ''}`.trim();

    return (
        <header className="app-header">
            <button
                type="button"
                className="app-header__toggle"
                onClick={toggleSidebar}
                aria-label={t('Menu')}
                aria-expanded={offcanvas}
                aria-controls="app-sidebar"
            >
                <i className="fa fa-bars" />
            </button>

            <Link to="/" className="app-header__brand">
                <img src={MyOpinionLogo} alt="" />
                <span>{process.env.REACT_APP_APP_NAME}</span>
            </Link>

            <div className="app-header__spacer" />

            <LanguageSwitcher className="app-header__language" />

            <Dropdown alignRight className="app-header__account">
                <Dropdown.Toggle variant="light" id="account-menu" className="app-header__account-toggle">
                    <span className="app-avatar" aria-hidden="true">{initials(user)}</span>
                    <span className="app-header__account-text">
                        <strong>{fullName}</strong>
                        <small>{roleLabel}</small>
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <div className="app-account-card">
                        <span className="app-avatar app-avatar--lg" aria-hidden="true">{initials(user)}</span>
                        <div>
                            <strong>{fullName}</strong>
                            <div className="text-muted small">{user?.email}</div>
                            <span className="badge badge-info mt-1">{roleLabel}</span>
                        </div>
                    </div>
                    <Dropdown.Divider />
                    <Dropdown.Item as={Link} to="/profile"><i className="icon-user mr-2" />{t('Mon profil')}</Dropdown.Item>
                    <Dropdown.Item as="button" type="button" onClick={logout}><i className="icon-power mr-2" />{t('Déconnexion')}</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            <div className="app-header__progress" aria-hidden="true">
                <div style={{ width: `${progress}%` }} />
            </div>
        </header>
    );
};

const mapStateToProps = (state) => ({
    user: state.auth.user,
    offcanvas: state.settings.offcanvas,
    miniSidebar: state.settings.miniSidebar,
});

const mapDispatchToProps = (dispatch) => ({
    setOffcanvas: (e) => dispatch(setOffcanvas(e)),
    setMiniSidebar: (e) => dispatch(setMiniSidebar(e)),
    loggedOut: () => dispatch(loggedOut()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
